// src/components/inventory/InventoryValuationWidget.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,

  Flex,
  useColorModeValue,
  Icon,
  Box,
  Text,
  VStack,
  HStack,
  Badge,
  Tooltip,
  IconButton,
  useToast,
  Skeleton,
  SkeletonText,
  Progress,
  Divider,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Collapse,
  Button,
} from '@chakra-ui/react';
import {
  MdAttachMoney,
  MdRefresh,


  MdInfo,
  MdMoreVert,

  MdCheckCircle,
  MdError,
  MdHistory,
} from 'react-icons/md';
import { InfoIcon, } from '@chakra-ui/icons';
import inventoryService from '../../../services/inventoryService';
// --- UPDATE: live refresh on inventory / work-order events (parts are consumed by jobs)
import useFlowSenseLive from '../../../hooks/useFlowSenseLive';
// --- END UPDATE
import Card from '../../card/Cards';

// Debug logger utility
const debugLog = (component, action, data, level = 'info') => {
  const timestamp = new Date().toISOString();
  const logEntry = {
    timestamp,
    component,
    action,
    data,
    level,
  };

  const styles = {
    info: 'color: #3182ce; font-weight: bold;',
    success: 'color: #38a169; font-weight: bold;',
    warning: 'color: #d69e2e; font-weight: bold;',
    error: 'color: #e53e3e; font-weight: bold;',
  };

  console.log(
    `%c[${timestamp}] [${component}] ${action}`,
    styles[level] || styles.info,
    data
  );

  // Store in session storage for debugging
  try {
    const logs = JSON.parse(sessionStorage.getItem('debug_logs') || '[]');
    logs.push(logEntry);
    // Keep only last 100 logs
    if (logs.length > 100) logs.shift();
    sessionStorage.setItem('debug_logs', JSON.stringify(logs));
  } catch (e) {
    console.error('Failed to store debug log:', e);
  }

  return logEntry;
};

const InventoryValuationWidget = ({ refreshInterval = 300000, showDetails = true }) => {
  // State management
  const [valuation, setValuation] = useState(0);
  const [previousValuation, setPreviousValuation] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [breakdown, setBreakdown] = useState(null);
  const [history, setHistory] = useState([]);
  const [, setTrend] = useState(null); // trend value reserved for a future sparkline
  const [showDebugInfo, setShowDebugInfo] = useState(false);

  // --- UPDATE: refs break the fetch loop. fetchValuation used to depend on [valuation, retryCount],
  // so every success/failure recreated it and the mount effect fetched again — forever on errors
  // (retryCount kept changing) and it also overwrote previousValuation with the same value.
  const valuationRef = useRef(0);
  const retryCountRef = useRef(0);
  const isMountedRef = useRef(true);
  const inflightRef = useRef(false);
  useEffect(() => { isMountedRef.current = true; return () => { isMountedRef.current = false; }; }, []);
  // --- END UPDATE

  const { isOpen: isModalOpen, onOpen: onModalOpen, onClose: onModalClose } = useDisclosure();
  const toast = useToast();

  // Theme colors
  const brandColor = useColorModeValue('brand.500', 'white');
  const boxBg = useColorModeValue('secondaryGray.300', 'whiteAlpha.100');
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.700', 'white');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const successColor = useColorModeValue('green.500', 'green.300');
  const errorColor = useColorModeValue('red.500', 'red.300');
  const historyItemHoverBg = useColorModeValue('gray.100', 'gray.600');

  // Calculate percentage change
  const percentageChange = useMemo(() => {
    if (!previousValuation || previousValuation === 0) return 0;
    return ((valuation - previousValuation) / previousValuation) * 100;
  }, [valuation, previousValuation]);

  // Determine trend direction
  const trendDirection = useMemo(() => {
    if (percentageChange > 0) return 'increase';
    if (percentageChange < 0) return 'decrease';
    return 'stable';
  }, [percentageChange]);

  // Format currency
  const formatCurrency = useCallback((value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  }, []);

  // Format percentage
  const formatPercentage = useCallback((value) => {
    return `${value > 0 ? '+' : ''}${value.toFixed(2)}%`;
  }, []);

  // Fetch valuation data
  // --- UPDATE: silent = live/auto refresh (no spinner over the current value)
  const fetchValuation = useCallback(async (isManualRefresh = false, silent = false) => {
    if (inflightRef.current) return;
    inflightRef.current = true;
    const retryCount = retryCountRef.current;
    const startTime = performance.now();
    debugLog('InventoryValuationWidget', 'fetchValuation:start', { isManualRefresh, retryCount });

    try {
      if (!silent) setIsLoading(true);
      setError(null);
  // --- END UPDATE

      const data = await inventoryService.getValuation();
      const endTime = performance.now();
      const fetchDuration = endTime - startTime;

      debugLog('InventoryValuationWidget', 'fetchValuation:success', {
        data,
        fetchDuration: `${fetchDuration.toFixed(2)}ms`,
        retryCount,
      }, 'success');

      // --- UPDATE: previous = last DIFFERENT value; total_value may arrive as a string
      if (!isMountedRef.current) return;
      const newValuation = Number(data?.total_value ?? data?.data?.total_value) || 0;
      if (newValuation !== valuationRef.current) {
        setPreviousValuation(valuationRef.current);
        valuationRef.current = newValuation;
      }
      setValuation(newValuation);
      // --- END UPDATE

      // Update breakdown if available
      if (data.breakdown) {
        setBreakdown(data.breakdown);
        debugLog('InventoryValuationWidget', 'breakdown:updated', data.breakdown);
      }

      // Update history
      const newHistoryEntry = {
        timestamp: new Date().toISOString(),
        value: newValuation,
      };
      setHistory(prev => [...prev.slice(-9), newHistoryEntry]);

      // Calculate trend
      if (data.trend !== undefined) {
        setTrend(data.trend);
      }

      setLastUpdated(new Date());
      retryCountRef.current = 0; // --- UPDATE
      setRetryCount(0);

      if (isManualRefresh) {
        toast({
          title: 'Data Refreshed',
          description: `Valuation updated: ${formatCurrency(newValuation)}`,
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      }

    } catch (error) {
      const endTime = performance.now();
      const fetchDuration = endTime - startTime;

      debugLog('InventoryValuationWidget', 'fetchValuation:error', {
        error: error.message,
        stack: error.stack,
        fetchDuration: `${fetchDuration.toFixed(2)}ms`,
        retryCount,
      }, 'error');

      if (!isMountedRef.current) return; // --- UPDATE
      setError(error.message || 'Failed to fetch inventory valuation');
      retryCountRef.current += 1; // --- UPDATE
      setRetryCount(retryCountRef.current);

      if (isManualRefresh) {
        toast({
          title: 'Refresh Failed',
          description: error.message || 'Failed to fetch inventory valuation',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        });
      }

    } finally {
      inflightRef.current = false; // --- UPDATE
      if (isMountedRef.current) setIsLoading(false);
    }
  }, [toast, formatCurrency]); // --- UPDATE: stable — no valuation / retryCount deps

  // Initial fetch and auto-refresh
  useEffect(() => {
    debugLog('InventoryValuationWidget', 'component:mounted', { refreshInterval });
    fetchValuation();

    if (refreshInterval > 0) {
      const intervalId = setInterval(() => {
        debugLog('InventoryValuationWidget', 'auto-refresh:triggered', { interval: refreshInterval });
        fetchValuation(false, true); // --- UPDATE: silent
      }, refreshInterval);

      return () => {
        debugLog('InventoryValuationWidget', 'component:unmounting', { intervalId });
        clearInterval(intervalId);
      };
    }
  }, [fetchValuation, refreshInterval]);

  // --- UPDATE: live refresh (inventory_updated, work orders consume parts)
  useFlowSenseLive(['inventory', 'workOrders'], () => fetchValuation(false, true), { debounceMs: 3000 });
  // --- END UPDATE

  // Manual refresh handler
  const handleRefresh = useCallback(() => {
    debugLog('InventoryValuationWidget', 'manual-refresh:triggered', {});
    fetchValuation(true);
  }, [fetchValuation]);

  // Export debug logs
  const exportDebugLogs = useCallback(() => {
    try {
      const logs = JSON.parse(sessionStorage.getItem('debug_logs') || '[]');
      const dataStr = JSON.stringify(logs, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `inventory-valuation-debug-${Date.now()}.json`;
      link.click();
      URL.revokeObjectURL(url);

      toast({
        title: 'Debug Logs Exported',
        description: 'Debug logs have been downloaded',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Failed to export debug logs:', error);
      toast({
        title: 'Export Failed',
        description: 'Failed to export debug logs',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }, [toast]);

  // Clear debug logs
  const clearDebugLogs = useCallback(() => {
    sessionStorage.removeItem('debug_logs');
    toast({
      title: 'Debug Logs Cleared',
      status: 'info',
      duration: 2000,
      isClosable: true,
    });
  }, [toast]);

  // Loading skeleton
  if (isLoading && !valuation) {
    return (
      <Card p="20px">
        <Flex align="center">
          <Skeleton
            w="56px"
            h="56px"
            borderRadius="50%"
            me="20px"
          />
          <VStack align="start" flex={1} spacing={2}>
            <SkeletonText noOfLines={1} width="100px" />
            <SkeletonText noOfLines={1} width="150px" />
            <SkeletonText noOfLines={1} width="120px" />
          </VStack>
        </Flex>
      </Card>
    );
  }

  return (
    <Card p="20px" position="relative" bg={cardBg} borderColor={borderColor} borderWidth="1px">
      {/* Header with actions */}
      <Flex justify="space-between" align="center" mb={4}>
        <HStack spacing={2}>
          <Tooltip label="Inventory Valuation Information" placement="top">
            <Box>
              <Icon as={MdInfo} color="gray.500" boxSize={4} />
            </Box>
          </Tooltip>
          {lastUpdated && (
            <Text fontSize="xs" color="gray.500">
              Updated {new Date(lastUpdated).toLocaleTimeString()}
            </Text>
          )}
        </HStack>

        <HStack spacing={2}>
          {/* Refresh button */}
          <Tooltip label="Refresh Data" placement="top">
            <IconButton
              icon={<MdRefresh />}
              size="sm"
              variant="ghost"
              isLoading={isLoading}
              onClick={handleRefresh}
              aria-label="Refresh valuation"
            />
          </Tooltip>

          {/* Menu for additional options */}
          <Menu>
            <MenuButton
              as={IconButton}
              icon={<MdMoreVert />}
              size="sm"
              variant="ghost"
              aria-label="More options"
            />
            <MenuList>
              <MenuItem icon={<MdHistory />} onClick={onModalOpen}>
                View History
              </MenuItem>
              <MenuItem icon={<InfoIcon />} onClick={() => setShowDebugInfo(!showDebugInfo)}>
                {showDebugInfo ? 'Hide' : 'Show'} Debug Info
              </MenuItem>
              <MenuItem onClick={exportDebugLogs}>
                Export Debug Logs
              </MenuItem>
              <MenuItem onClick={clearDebugLogs}>
                Clear Debug Logs
              </MenuItem>
            </MenuList>
          </Menu>
        </HStack>
      </Flex>

      {/* Error Alert */}
      {error && (
        <Alert status="error" mb={4} borderRadius="md">
          <AlertIcon />
          <Box flex={1}>
            <AlertTitle fontSize="sm">Error Loading Data</AlertTitle>
            <AlertDescription fontSize="xs">{error}</AlertDescription>
          </Box>
          <Button size="xs" onClick={handleRefresh} ml={2}>
            Retry
          </Button>
        </Alert>
      )}

      {/* Main Content */}
      <Flex align="center" mb={showDetails ? 4 : 0}>
        <Box
          w="56px"
          h="56px"
          bg={boxBg}
          me="20px"
          borderRadius="50%"
          display="flex"
          alignItems="center"
          justifyContent="center"
          position="relative"
          transition="all 0.3s"
          _hover={{ transform: 'scale(1.05)' }}
        >
          <Icon w="32px" h="32px" as={MdAttachMoney} color={brandColor} />
          
          {/* Status indicator */}
          <Box
            position="absolute"
            bottom="0"
            right="0"
            w="16px"
            h="16px"
            borderRadius="50%"
            bg={error ? errorColor : successColor}
            border="2px solid"
            borderColor={cardBg}
          >
            <Icon
              as={error ? MdError : MdCheckCircle}
              w="12px"
              h="12px"
              color="white"
            />
          </Box>
        </Box>

        <Stat flex={1}>
          <StatLabel fontSize="sm" color={textColor}>
            Inventory Value
            {retryCount > 0 && (
              <Badge ml={2} colorScheme="orange" fontSize="xs">
                {retryCount} {retryCount === 1 ? 'retry' : 'retries'}
              </Badge>
            )}
          </StatLabel>

          <HStack spacing={3} align="baseline">
            <StatNumber fontSize="2xl" fontWeight="bold" color={textColor}>
              {formatCurrency(valuation)}
            </StatNumber>

            {/* Trend indicator */}
            {previousValuation > 0 && trendDirection !== 'stable' && (
              <HStack spacing={1}>
                <StatArrow type={trendDirection} />
                <Text
                  fontSize="sm"
                  color={trendDirection === 'increase' ? successColor : errorColor}
                  fontWeight="semibold"
                >
                  {formatPercentage(Math.abs(percentageChange))}
                </Text>
              </HStack>
            )}
          </HStack>

          <StatHelpText fontSize="xs" color="gray.500" mt={1}>
            Total value of current stock
            {breakdown && (
              <Text as="span" ml={1}>
                ({breakdown.item_count || 0} items)
              </Text>
            )}
          </StatHelpText>
        </Stat>
      </Flex>

      {/* Details Section */}
      {showDetails && breakdown && (
        <>
          <Divider my={3} />
          
          <VStack align="stretch" spacing={2}>
            {/* Category breakdown */}
            {breakdown.by_category && Object.keys(breakdown.by_category).length > 0 && (
              <Box>
                <Text fontSize="xs" fontWeight="semibold" mb={2} color={textColor}>
                  Top Categories
                </Text>
                <VStack align="stretch" spacing={1}>
                  {Object.entries(breakdown.by_category)
                    .sort((a, b) => b[1] - a[1])
                    .slice(0, 3)
                    .map(([category, value]) => (
                      <Flex key={category} justify="space-between" fontSize="xs">
                        <Text color="gray.600">{category}</Text>
                        <Text fontWeight="semibold" color={textColor}>
                          {formatCurrency(value)}
                        </Text>
                      </Flex>
                    ))}
                </VStack>
              </Box>
            )}

            {/* Progress bar for stock levels */}
            {breakdown.stock_percentage !== undefined && (
              <Box>
                <Flex justify="space-between" fontSize="xs" mb={1}>
                  <Text color="gray.600">Stock Level</Text>
                  <Text fontWeight="semibold" color={textColor}>
                    {breakdown.stock_percentage.toFixed(0)}%
                  </Text>
                </Flex>
                <Progress
                  value={breakdown.stock_percentage}
                  size="sm"
                  colorScheme={
                    breakdown.stock_percentage > 75
                      ? 'green'
                      : breakdown.stock_percentage > 50
                      ? 'blue'
                      : breakdown.stock_percentage > 25
                      ? 'orange'
                      : 'red'
                  }
                  borderRadius="md"
                />
              </Box>
            )}
          </VStack>
        </>
      )}

      {/* Debug Information */}
      <Collapse in={showDebugInfo} animateOpacity>
        <Box
          mt={4}
          p={3}
          borderRadius="md"
          fontSize="xs"
          fontFamily="mono"
        >
          <Text fontWeight="bold" mb={2} color={textColor}>
            Debug Information
          </Text>
          <VStack align="stretch" spacing={1}>
            <Flex justify="space-between">
              <Text color="gray.600">Component State:</Text>
              <Badge colorScheme={error ? 'red' : 'green'}>
                {error ? 'Error' : isLoading ? 'Loading' : 'Ready'}
              </Badge>
            </Flex>
            <Flex justify="space-between">
              <Text color="gray.600">Last Fetch:</Text>
              <Text color={textColor}>
                {lastUpdated ? new Date(lastUpdated).toISOString() : 'Never'}
              </Text>
            </Flex>
            <Flex justify="space-between">
              <Text color="gray.600">Retry Count:</Text>
              <Text color={textColor}>{retryCount}</Text>
            </Flex>
            <Flex justify="space-between">
              <Text color="gray.600">Refresh Interval:</Text>
              <Text color={textColor}>{refreshInterval}ms</Text>
            </Flex>
            <Flex justify="space-between">
              <Text color="gray.600">History Entries:</Text>
              <Text color={textColor}>{history.length}</Text>
            </Flex>
            {error && (
              <Box mt={2} p={2} bg="red.50" borderRadius="md">
                <Text color="red.600" fontSize="xs">
                  {error}
                </Text>
              </Box>
            )}
          </VStack>
        </Box>
      </Collapse>

      {/* History Modal */}
      <Modal isOpen={isModalOpen} onClose={onModalClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Valuation History</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {history.length > 0 ? (
              <VStack align="stretch" spacing={2}>
                {history.map((entry, index) => (
                  <Flex
                    key={index}
                    justify="space-between"
                    p={2}
                    bg={hoverBg}
                    borderRadius="md"
                    _hover={{ bg: historyItemHoverBg }}
                  >
                    <Text fontSize="sm" color="gray.600">
                      {new Date(entry.timestamp).toLocaleString()}
                    </Text>
                    <Text fontSize="sm" fontWeight="semibold" color={textColor}>
                      {formatCurrency(entry.value)}
                    </Text>
                  </Flex>
                ))}
              </VStack>
            ) : (
              <Text color="gray.500" textAlign="center" py={8}>
                No history data available yet
              </Text>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Card>
  );
};

export default InventoryValuationWidget;